// services/AssignTestsService.js
import { api } from "../../services/NetworkBase";

/**
 * Get all test categories
 * Matches VB.NET ShowTestCats functionality
 * @returns {Promise<Object>} - { results: TestCategoryDto[] }
 */
export async function GetTestCategories() {
  try {
    const response = await api.get("vlp/assign-tests/categories").json();
    return response;
  } catch (error) {
    console.error("Error in GetTestCategories:", error);
    throw new Error(error.message || "Failed to get test categories");
  }
}

/**
 * Search for tests matching criteria
 * Matches VB.NET SelectTests functionality
 * @param {Object} request - TestSearchRequest
 * @param {string} request.queryArg - Search query string (searches test_code, parameter, method_code)
 * @param {string} request.category - Test category filter
 * @param {string} request.matrix - Sample matrix filter
 * @param {string} request.sortBy - Sort column (optional)
 * @returns {Promise<Object>} - { results: TestSearchResultDto[] }
 */
export async function SearchTests(request) {
  try {
    const response = await api
      .post("vlp/assign-tests/search", {
        json: {
          queryArg: request.queryArg || "",
          category: request.category || "(All Categories)",
          matrix: request.matrix || "All",
          sortBy: request.sortBy || "parameter"
        },
      })
      .json();
    return response;
  } catch (error) {
    console.error("Error in SearchTests:", error);
    throw new Error(error.message || "Failed to search tests");
  }
}

/**
 * Assign tests to item (Sample, Quote, Order, Slate, Process)
 * Matches VB.NET AddTests functionality with all Case statements
 * 
 * IMPORTANT BEHAVIOR NOTES:
 * - For "Sample": Only validates tests and returns them - parent page handles DB insert
 * - For "Quote": Creates quotrans records with test pricing
 * - For "Order": Creates ordered_bottles records with bottle/preservation info
 * - For "Slate": Creates jobtests records with sort_order
 * - For "Process": Creates processtestsettings records
 * 
 * @param {Object} request - AssignTestsRequest
 * @param {string} request.itemType - "Sample" | "Quote" | "Order" | "Slate" | "Process"
 * @param {string} request.itemValue - Item identifier (sample number, quote number, order number, job_id, process_id)
 * @param {string[]} request.testCodes - Array of test codes to assign
 * @returns {Promise<Object>} - { success, message, assignedTests, assignedCount }
 */
export async function AssignTests(request) {
  try {
    if (!request.itemType) {
      throw new Error("Item type is required");
    }

    if (!request.itemValue) {
      throw new Error("Item value is required");
    }

    if (!request.testCodes || request.testCodes.length === 0) {
      throw new Error("No tests selected");
    }

    const response = await api
      .post("vlp/assign-tests/assign", {
        json: {
          itemType: request.itemType,
          itemValue: request.itemValue,
          testCodes: request.testCodes,
        },
      })
      .json();
    return response;
  } catch (error) {
    console.error("Error in AssignTests:", error);
    throw new Error(error.message || "Failed to assign tests");
  }
}